import {CV_8UC4, ImageData, Mat} from "mirada";
import * as gm from "gammacv";
import {CBARPipeline} from "./pipeline/CBARPipeline";
import {TensorDataView} from "gammacv";
import {matRelease, matRetain} from "./Utils";

export let framesAlive = 0;

/**
 * Obtain a quartered up image from a custom shader
 *
 Requires custom shader to place these same quarter's
 green channel (or b/w calculated) into r,g,b,a channels,
 permitting the download of only the top corner quadrant of the image
 which now contains all the bytes necessary to reconstruct the greyscale image
 This takes one fourth the memory and time
 **/
const dequarterImage = (width:number, height:number, data:TensorDataView) =>{
    const result = new cv.Mat(height * 2, width * 2, cv.CV_8UC1);
    const rawImage = cv.matFromArray(height, width, cv.CV_8UC4, data);

    const quarters = new cv.MatVector();
    cv.split(rawImage, quarters);

    //top left
    let roi = result.roi({x:0, y:0, width:width, height:height});
    let src = quarters.get(0);
    src.copyTo(roi);
    src.delete();
    roi.delete();

    //top right
    roi = result.roi({x:width, y:0, width:width, height:height});
    src = quarters.get(1);
    src.copyTo(roi);
    src.delete();
    roi.delete();

    //bottom right
    roi = result.roi({x:width, y:height, width:width, height:height})
    src = quarters.get(2);
    src.copyTo(roi);
    src.delete();
    roi.delete();

    //bottom left
    roi = result.roi({x:0, y:height, width:width, height:height})
    src = quarters.get(3);
    src.copyTo(roi);
    src.delete();
    roi.delete();

    quarters.delete();
    rawImage.delete();

    return result;
}

export class CBARFrame {
    constructor(
        public readonly context:CBARPipeline,
        public readonly index:number,
        public readonly time = performance.now()) {

        framesAlive += 1;

        if (framesAlive > 30) {
            throw "Too many frames";
        }
    }

    get rgbOutput() {
        if (!this._hasRGB && this.context.session && this.context.colorOp && this.context.colorOutput) {
            this._hasRGB = true;
            this.context.session.runOp(this.context.colorOp, this.index, this.context.colorOutput);
        }
        return this.context.colorOutput;
    }; private _hasRGB = false;

    get rgbImage() {
        if (!this._rgbImage && this.rgbOutput) {
            this._rgbImage = cv.matFromArray(this.rgbOutput.shape[0], this.rgbOutput.shape[1], cv.CV_8UC4, this.rgbOutput.data);
            cv.cvtColor(this._rgbImage, this._rgbImage, cv.COLOR_RGBA2RGB, 0);
            matRetain(this._rgbImage);
        }

        return this._rgbImage!;
    }; private _rgbImage?:Mat;

    get quarteredOutput() {
        if (!this._hasQuartered && this.context.session && this.context.quarteredOp && this.context.quarteredOutput) {
            this._hasQuartered = true;
            this.context.session.runOp(this.context.quarteredOp, this.index, this.context.quarteredOutput);
        }
        return this.context.quarteredOutput;
    }; private _hasQuartered = false;

    get greyscaleOutput() {
        if (!this._hasGreyscale && this.context.session && this.context.greyscaleOp && this.context.greyscaleOutput) {
            this._hasGreyscale = true;
            this.context.session.runOp(this.context.greyscaleOp, this.index, this.context.greyscaleOutput);
        }
        return this.context.greyscaleOutput;
    }; private _hasGreyscale = false;

    get greyscaleImage() {
        if (!this._greyscaleImage) {
            if (this.quarteredOutput) {
                this._greyscaleImage = dequarterImage(this.quarteredOutput.shape[1], this.quarteredOutput.shape[0], this.quarteredOutput.data);
            } else if (this.greyscaleOutput) {
                this._greyscaleImage = cv.matFromArray(this.greyscaleOutput.shape[0], this.greyscaleOutput.shape[1], cv.CV_8UC4, this.greyscaleOutput.data);
                cv.cvtColor(this._greyscaleImage, this._greyscaleImage, cv.COLOR_RGBA2GRAY, 0);
            }
            matRetain(this._greyscaleImage!);
        }

        return this._greyscaleImage!;
    }; private _greyscaleImage?:Mat;

    get edgesOutput() {
        if (!this._hasEdges && this.context.session && this.context.edgesOp && this.context.edgesOutput) {
            this._hasEdges = true;
            this.context.session.runOp(this.context.edgesOp, this.index, this.context.edgesOutput);
        }
        return this.context.edgesOutput;
    }; private _hasEdges = false;

    get edgesImage() {
        if (!this._edgesImage && this.edgesOutput) {
            this._edgesImage = cv.matFromArray(this.edgesOutput.shape[0], this.edgesOutput.shape[1], cv.CV_8UC4, this.edgesOutput.data);
            cv.cvtColor(this._edgesImage, this._edgesImage, cv.COLOR_RGBA2GRAY, 0);
        }

        return this._edgesImage!;
    }; private _edgesImage?:Mat;

    get pcLinesOutput() {
        if (!this._hasPCLinesOutput && this.context.session && this.context.pcLinesOutput) {
            this._hasPCLinesOutput = true;
            this.context.session.runOp(this.context.pipeline as gm.Operation, this.index, this.context.pcLinesOutput);
        }
        return this.context.pcLinesOutput;
    }; private _hasPCLinesOutput = false;

    get colorSegmentationOutput() {
        if (!this._hasColorSegmentationOutput && this.context.session && this.context.pipeline && this.context.colorSegmentationOp && this.context.colorSegmentationOutput) {
            this._hasColorSegmentationOutput = true;
            this.context.session.runOp(this.context.colorSegmentationOp, this.index, this.context.colorSegmentationOutput);
        }
        return this.context.colorSegmentationOutput;
    }; private _hasColorSegmentationOutput = false;

    get colorSegmentationImage() {
        if (!this._colorSegmentationImage && this.colorSegmentationOutput) {
            this._colorSegmentationImage = cv.matFromArray(this.colorSegmentationOutput.shape[0], this.colorSegmentationOutput.shape[1], cv.CV_8UC4, this.colorSegmentationOutput.data);
            cv.cvtColor(this._colorSegmentationImage, this._colorSegmentationImage, cv.COLOR_RGBA2GRAY, 0);
            matRetain(this._colorSegmentationImage);
        }
        return this._colorSegmentationImage!;
    }; private _colorSegmentationImage?:Mat;

    public destroy() {

        framesAlive -= 1;

        if (this._rgbImage) {
            matRelease(this._rgbImage);
            this._rgbImage = undefined;
        }

        if (this._greyscaleImage) {
            matRelease(this._greyscaleImage);
            this._greyscaleImage = undefined;
        }

        // if (this._edgesImage) {
        //     matRelease(this._edgesImage);
        //     this._edgesImage = undefined;
        // }

        if (this._colorSegmentationImage) {
            matRelease(this._colorSegmentationImage);
            this._colorSegmentationImage = undefined;
        }
    }

    public clone() {
        const copy = new CBARFrame(this.context, this.index, this.time);
        copy._rgbImage = this._rgbImage?.clone();
        copy._greyscaleImage = this._greyscaleImage?.clone();
        //copy._edgesImage = this._edgesImage?.clone();
        copy._colorSegmentationImage = this._colorSegmentationImage?.clone();
        return copy;
    }
}